/* eslint-disable jsx-a11y/control-has-associated-label */
/* eslint-disable jsx-a11y/anchor-has-content */
import { useCallback, useEffect, useMemo, useState, useTransition } from 'react';
import isEmpty from 'lodash/isEmpty';
import type { Hit } from '@algolia/client-search';
import Link from 'next/link';
import styled from 'styled-components';
import { Trans, useTranslation } from 'next-i18next';

import StyledContainer from '@design-system/styled/Container';
import SimilarCars from '@source/components/SimilarCars';
import useAlgoliaService from '@source/hooks/useAlgoliaService';
import useRegionConfig from '@source/hooks/useRegionConfig';
import PopularCarSkeleton from '@source/pages/SellConfirmation/components/PopularCars/PopularCars.skeleton';
import { Button } from '@design-system/components';

import { URL_CONFIG } from '@source/constants/urlConfig';
import { IRegionProps } from '@source/interface';
import { THEME } from '@source/constants/theme';
import { getCCPOQueryStringUrl } from '../utils';

const StyledBrowseCard = styled.div`
  margin-bottom: 60px;

  .browse-card-title {
    ${(props) => props.theme.typo.familyGoogle.regular}
    font-weight: 400;
    > span {
      color: ${(props) => props.theme.color.primaryBase};
    }

    strong {
      ${(props) => props.theme.typo.familyGoogle.semiBold}
    }
  }

  @media (min-width: 768px) {
    margin-bottom: 69px;
  }
`;

const StyledLinkWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding-top: 12px;
  display: flex;
  justify-content: center;
  ${(props) => props.theme.typo.familyGoogle.semiBold}

  .ant-btn {
    width: 100%;
  }

  @media (min-width: 768px) {
    padding-top: 32px;

    .ant-btn {
      height: 42px;

      width: unset;
      line-height: 22px;
    }
  }
`;

const CCPOBrowseCards = ({ region }: IRegionProps) => {
  const [cars, setCars] = useState<Hit<object>[]>([]);
  const { fetchAlgoliaData } = useAlgoliaService({});
  const [isPending, startTransition] = useTransition();
  const { i18n, t } = useTranslation('ccpo');
  const [currentLang, setCurrentLang] = useState(i18n.language);

  i18n.on('languageChanged', (lng) => {
    setCurrentLang(lng);
  });

  const {
    countryConfig: { country },
  } = useRegionConfig();

  // Get algolia filter
  const filterConfigs = useMemo(() => {
    switch (region) {
      case 'th':
      case 'my':
        return `listing.flags.is_ccpo=1`;
      case 'hk':
      case 'jp':
      case 'id':
      case 'sg':
      default:
        return 'listing.is_ccpo=1';
    }
  }, [region]);

  const getCars = useCallback(() => {
    startTransition(() => {
      const params = {
        facets: ['*'],
        filters: `((${filterConfigs}))`,
        hitsPerPage: 9,
      };

      fetchAlgoliaData(params).then((res) => {
        setCars(res.hits);
      });
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (country) {
      getCars();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [country]);

  const tilte = useMemo(() => {
    const components = {
      a: (
        <a
          href={
            URL_CONFIG({
              region,
              lang: currentLang,
              query: getCCPOQueryStringUrl(region),
            }).formatted
          }
          // target="_blank"
          // rel="noreferrer"
        />
      ),
      strong: <strong />,
      marginLeft: <span style={{ marginLeft: '4px' }} />,
      specificFont: <span style={{ fontFamily: 'Poppins' }} />,
      blackText: <span style={{ color: THEME.color.onSurface }} />,
    };
    return (
      <div className="browse-card-title">
        <Trans
          ns="ccpo"
          defaults={region === 'my' ? 'ccpo.browseCards.titleMY' : 'ccpo.browseCards.title'}
          components={components}
        />
      </div>
    );
  }, [currentLang]);

  return (
    <StyledContainer>
      {!isEmpty(cars) && (
        <StyledBrowseCard>
          <div className="checkoutcars-container">
            {isPending && <PopularCarSkeleton />}
            {!isPending && <SimilarCars data={cars} title={tilte} responsiveTitle={tilte} responsiveSubTitle="" />}
          </div>
          <StyledLinkWrapper>
            <Link
              // passHref
              href={
                URL_CONFIG({
                  region,
                  lang: currentLang,
                  query: getCCPOQueryStringUrl(region),
                }).formatted
              }
              legacyBehavior
            >
              <Button type="primary" block variant="contained">
                <Trans
                  ns="ccpo"
                  defaults={t(
                    ['my', 'jp', 'hk'].includes(region) ? 'ccpo.browseCards.buttonMY' : 'ccpo.browseCards.button',
                  )}
                />
              </Button>
            </Link>
          </StyledLinkWrapper>
        </StyledBrowseCard>
      )}
    </StyledContainer>
  );
};

export default CCPOBrowseCards;
